function extractDigits(str: string): string {
	return str.replace(/\D/g, '');
}

const AREA_CODE_LENGTH = 3;
const PREFIX_LENGTH = 3;
const LINE_NUMBER_LENGTH = 4;
const SIX_DIGITS = AREA_CODE_LENGTH + PREFIX_LENGTH;
const NATIONAL_NUMBER_LENGTH = AREA_CODE_LENGTH + PREFIX_LENGTH + LINE_NUMBER_LENGTH;
const DEFAULT_COUNTRY_CALLING_CODE = 1;

function resolveCountryCallingCode(countryCode: number): number {
	if (typeof countryCode !== 'number' || !Number.isFinite(countryCode)) {
		return DEFAULT_COUNTRY_CALLING_CODE;
	}

	return countryCode;
}

function stripCountryCallingCode(digits: string, countryCode: number): string {
	const prefix = String(countryCode);
	const expectedLength = prefix.length + NATIONAL_NUMBER_LENGTH;
	if (digits.startsWith(prefix) && digits.length === expectedLength) {
		return digits.slice(prefix.length);
	}
	return digits;
}

/**
 * Formats any input string as a national phone number.
 *
 * Extracts digits from the input and returns them in (XXX) XXX-XXXX format.
 * When the digit string is a national number plus `countryCode`, the calling
 * code is stripped first. `countryCode` defaults to 1 (NANP: US, Canada, etc.).
 * - (XXX) XXX-XXXX for 7 or more digits
 * - (XXX) XXX for 4-6 digits
 * - XXX for 3 or fewer digits
 *
 * @param phoneNumber - Any string containing a phone number.
 * @param countryCode - ITU calling code used to detect a leading country prefix.
 * @returns The formatted national phone number string.
 *
 * @example
 * ```typescript
 * formatPhoneNumber("abc1234567890xyz"); // "(123) 456-7890"
 * formatPhoneNumber("+14090178290");     // "(409) 017-8290"
 * formatPhoneNumber("+442071838750", 44); // "(207) 183-8750"
 * formatPhoneNumber("555-1234");         // "(555) 123-4"
 * formatPhoneNumber("12a");              // "12"
 * ```
 */
function formatPhoneNumber(phoneNumber?: string, countryCode: number = DEFAULT_COUNTRY_CALLING_CODE): string {
	if (!phoneNumber) {
		return '';
	}

	const callingCode: number = resolveCountryCallingCode(countryCode);

	const onlyNums: string = stripCountryCallingCode(extractDigits(phoneNumber), callingCode);

	if (onlyNums.length <= AREA_CODE_LENGTH) {
		return onlyNums;
	}

	if (onlyNums.length <= SIX_DIGITS) {
		return `(${onlyNums.slice(0, AREA_CODE_LENGTH)}) ${onlyNums.slice(AREA_CODE_LENGTH)}`;
	}

	const areaCode: string = onlyNums.slice(0, AREA_CODE_LENGTH);
	const prefix: string = onlyNums.slice(AREA_CODE_LENGTH, SIX_DIGITS);
	const lineNumber: string = onlyNums.slice(SIX_DIGITS, SIX_DIGITS + LINE_NUMBER_LENGTH);

	return `(${areaCode}) ${prefix}-${lineNumber}`;
}

export { formatPhoneNumber };
